import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Comment {
  id: string;
  user_id: string;
  review_id: string;
  comment_text: string;
  created_at: string;
  parent_comment_id: string | null;
  mentioned_user_ids: string[];
  profiles: {
    username: string;
    name: string;
    image: string;
  };
  replies?: Comment[];
}

// Extract @username mentions from text
function extractMentions(text: string, allUsers: { id: string; username: string }[]): string[] {
  const mentionRegex = /@(\w+)/g;
  const matches = text.match(mentionRegex);
  if (!matches) return [];
  
  const usernames = matches.map(m => m.substring(1).toLowerCase());
  const mentionedUserIds = allUsers
    .filter(u => usernames.includes(u.username.toLowerCase()))
    .map(u => u.id);
  
  return mentionedUserIds;
}

export function useComments(reviewId: string) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchComments();
  }, [reviewId]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('review_comments')
        .select(`
          *,
          profiles (
            username,
            name,
            image
          )
        `)
        .eq('review_id', reviewId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Build threaded structure
      const commentMap = new Map<string, Comment>();
      const rootComments: Comment[] = [];

      (data || []).forEach(comment => {
        commentMap.set(comment.id, { ...comment as Comment, replies: [] });
      });

      (data || []).forEach(comment => {
        const commentWithReplies = commentMap.get(comment.id)!;
        if (comment.parent_comment_id) {
          const parent = commentMap.get(comment.parent_comment_id);
          if (parent) {
            parent.replies!.push(commentWithReplies);
          } else {
            rootComments.push(commentWithReplies);
          }
        } else {
          rootComments.push(commentWithReplies);
        }
      });

      setComments(rootComments);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const addComment = async (commentText: string, parentCommentId?: string) => {
    setSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Please sign in",
          description: "You need to be signed in to comment.",
          variant: "destructive",
        });
        return;
      }

      // Get all users for mention extraction
      const { data: allUsers } = await supabase
        .from('profiles')
        .select('id, username')
        .not('username', 'is', null);

      const mentionedUserIds = extractMentions(commentText.trim(), allUsers || []);

      const { error } = await supabase
        .from('review_comments')
        .insert({
          review_id: reviewId,
          user_id: user.id,
          comment_text: commentText.trim(),
          parent_comment_id: parentCommentId || null,
          mentioned_user_ids: mentionedUserIds,
        });

      if (error) throw error;

      // Send notifications to mentioned users
      if (mentionedUserIds.length > 0) {
        const { data: reviewData } = await supabase
          .from('user_books')
          .select('book_id')
          .eq('id', reviewId)
          .single();

        if (reviewData) {
          const { data: bookData } = await supabase
            .from('books')
            .select('title, cover_url')
            .eq('id', reviewData.book_id)
            .single();

          if (bookData) {
            for (const mentionedUserId of mentionedUserIds) {
              if (mentionedUserId !== user.id) {
                await supabase.from('notifications').insert({
                  user_id: mentionedUserId,
                  type: 'mention',
                  title: 'You were mentioned in a review comment',
                  message: `@${user.id} mentioned you in a comment on ${bookData.title}`,
                  link: `/`, // Could be improved with direct link
                  metadata: {
                    review_id: reviewId,
                    book_id: reviewData.book_id,
                    commenter_id: user.id
                  }
                });
              }
            }

            // Log activity for AI learning
            await supabase.from('activities').insert({
              user_id: user.id,
              type: 'review_commented',
              book_id: reviewData.book_id,
              book_title: bookData.title,
              book_cover_url: bookData.cover_url,
              payload: {
                review_id: reviewId,
                comment_excerpt: commentText.trim().substring(0, 140),
                parent_comment_id: parentCommentId
              }
            });
          }
        }
      }
      
      // Refresh comments
      await fetchComments();
      
      toast({
        title: "Comment added",
        description: "Your comment has been posted successfully.",
      });
    } catch (error: any) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add comment.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return {
    comments,
    commentCount: comments.length,
    loading,
    submitting,
    addComment,
    refreshComments: fetchComments
  };
}